import { darken } from "polished";
import { type FC } from "react";

import { SequenceType } from "./SensorAlnInfoCard.js";
import { SEQ_RESIDUE_AA_COLORS, SEQ_RESIDUE_NU_COLORS, STRAND_TYPE } from "@/settings/settings.ts";

export interface IColoredSequenceProps {
  offset: number; // one-based sequence position offset for this part (line) of the hsp alignment.
  seq: string;
  type: SequenceType;
  strand?: string;
  mismatches?: string;
}

/**
 * Creates one line of alignment residues (SequenceType type: DNA or AA)
 *
 * Sets the colors of residues via CSS:
 *  className = seq-residue-dna-(a|c|g|t)
 *  className = seq-residue-aa-(r|k|e|d|i|l|v|a|c|h|m|n|q|f|y|w|s|t|g|p)
 *
 * Creates tooltips using the title tag of spans showing coordinates of
 * the respective residue in query as well as in subject.
 */
const ColoredSequence: FC<IColoredSequenceProps> = (props) => {
  return (
    <span className="aln-line-content-col">
      {genResidueSpans(props.offset, props.type, props.seq, props.strand, props.mismatches)}
    </span>
  );
};

function genResidueSpans(offset: number, type: SequenceType, seq: string, strand: string, mismatches: string) {
  if (type === SequenceType.DNA) {
    return genResidueSpansWithColorScheme("dna", offset, seq, strand, mismatches);
  } else if (type === SequenceType.AMINOACID) {
    return genResidueSpansWithColorScheme("aa", offset, seq, strand, mismatches);
  } else {
    throw Error("Unknown sequence type: " + type);
  }
}

function genResidueSpansWithColorScheme(
  seqType: string,
  offset: number,
  seq: string,
  strand: string,
  mismatches: string,
) {
  const residueSpans = [];
  let gaps = 0;
  for (let i = 0; i < seq.length; ++i) {
    let span;
    const pos = !strand || strand === STRAND_TYPE.PLUS ? offset + i - gaps : offset - i + gaps;
    if (seq.charAt(i) !== " " && seq.charAt(i) !== "+" && seq.charAt(i) !== "|") {
      if (seq.charAt(i) === "-") {
        span = <span key={`seq-residue-${i}`}>{seq.charAt(i)}</span>;
        ++gaps;
      } else {
        const style =
          !mismatches || (!!mismatches && mismatches.charAt(i) === "1")
            ? seqType === "aa"
              ? { backgroundColor: SEQ_RESIDUE_AA_COLORS[seq.charAt(i).toLowerCase()] }
              : { backgroundColor: SEQ_RESIDUE_NU_COLORS[seq.charAt(i).toLowerCase()] }
            : null;
        span = (
          <span key={`seq-residue-${i}`} style={style} title={`Pos ${pos}`}>
            {seq.charAt(i)}
          </span>
        );
      }
    } else if (seq.charAt(i) === " ") {
      span = <span key={`seq-residue-${i}`}>&nbsp;</span>;
    } else {
      span = <span key={`seq-residue-${i}`}>{seq.charAt(i)}</span>;
    }
    residueSpans.push(span);
  }
  return residueSpans;
}

// FIXME: Merge!

// Render nucleotides with colors
const Nucleotides: FC<{ seq: string }> = ({ seq }) => {
  if (seq == undefined) return null;

  const length = seq.length;

  if (length > 140) {
    // It doesn't make sens to show same because of the high error rate of long reads
    // It 3 lines of bases in the tooltip
    return <>{length} bp</>;
  }

  return (
    <>
      {[...seq].map((nuc, index) => (
        <Nucleotide key={`nuc-${nuc}-${index}`} letter={nuc} hideLetter={length > 20} />
      ))}
    </>
  );
};

const Nucleotide: FC<{ letter: string; hideLetter?: boolean }> = ({ letter, hideLetter }) => {
  const nuc = letter.toUpperCase();
  return (
    <div
      style={{
        fontFamily: "Roboto Flex Mono, monospace",
        fontSize: 11,
        lineHeight: 1,
        height: 13,
        display: "inline-block",
        padding: "1px 3px 0",
        backgroundColor: SEQ_RESIDUE_NU_COLORS[nuc.toLowerCase()],
        boxShadow:
          hideLetter === true ? `-1px 0 0 0 ${darken(0.2, SEQ_RESIDUE_NU_COLORS[nuc.toLowerCase()])}` : undefined,
      }}
    >
      {hideLetter !== true ? nuc : null}
    </div>
  );
};

export { ColoredSequence, Nucleotide, Nucleotides };
